import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { OnboardingStepper } from '@/components/onboarding/OnboardingStepper';
import { OnboardingWelcome } from '@/components/onboarding/OnboardingWelcome';
import { OnboardingHighlights } from '@/components/onboarding/OnboardingHighlights';
import { OnboardingConfig } from '@/components/onboarding/OnboardingConfig';
import { OnboardingConfirm } from '@/components/onboarding/OnboardingConfirm';
import { useOnboarding } from '@/hooks/useOnboarding';
import { CheckCircle2, Loader2, AlertTriangle, ShieldAlert, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';
import { motion, AnimatePresence } from 'framer-motion';
import logoNetuno from '@/assets/logo_netuno.png';
import LoginBackground from '@/components/auth/LoginBackground';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    state, loading, error, authenticated,
    update, nextStep, prevStep, provision, validateCnpj,
  } = useOnboarding();

  const navigateWithPreviewToken = (path: string, replace = false) =>
    navigate(buildPathWithPreviewToken(path), { replace });

  useEffect(() => {
    if (state.status === 'completed') {
      const timer = setTimeout(() => navigateWithPreviewToken('/empreendimentos', true), 3000);
      return () => clearTimeout(timer);
    }
  }, [state.status]);

  if (authenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (authenticated === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-4 max-w-sm"
        >
          <div className="w-16 h-16 rounded-2xl bg-warning/10 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-8 h-8 text-warning" />
          </div>
          <h1 className="text-2xl font-bold">Acesso restrito</h1>
          <p className="text-muted-foreground text-sm">
            O onboarding do NetUno é acessível apenas por convite de um administrador.
            Verifique seu e-mail para o link de acesso.
          </p>
          <Button variant="outline" onClick={() => navigateWithPreviewToken('/auth')}>
            Ir para login
          </Button>
        </motion.div>
      </div>
    );
  }

  if (state.status === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="text-center space-y-5 max-w-sm"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto"
          >
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </motion.div>
          <h1 className="text-2xl font-bold">Configuração concluída!</h1>
          <div className="rounded-xl border bg-card p-4 space-y-2 text-left">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Building2 className="w-4 h-4 text-primary" />
              {state.empresa_nome}
            </div>
            {state.segmento && (
              <p className="text-xs text-muted-foreground pl-6">{state.segmento}</p>
            )}
            {state.cnpj && (
              <p className="text-xs text-muted-foreground pl-6">CNPJ: {state.cnpj}</p>
            )}
          </div>
          <p className="text-muted-foreground text-sm">Seu ambiente está pronto. Redirecionando…</p>
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" />
        </motion.div>
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 max-w-sm"
        >
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold">Falha na configuração</h1>
          <p className="text-muted-foreground text-sm">{error || 'Erro desconhecido.'}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-6 relative overflow-hidden">
      <LoginBackground />
      {/* Brand header with logo */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5 flex items-center gap-3 relative z-10"
      >
        <img src={logoNetuno} alt="NetUno" className="w-20 h-20 object-contain" />
        <span className="font-display font-bold text-2xl tracking-wide" style={{ color: 'hsl(var(--brand))' }}>NetUno</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="w-full max-w-2xl relative z-10"
      >
        <Card className="rounded-2xl border shadow-card overflow-hidden">
          <CardContent className="p-6 lg:p-8 space-y-5">
            <OnboardingStepper currentStep={state.currentStep} />

            <AnimatePresence mode="wait">
              <motion.div
                key={state.currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                {state.currentStep === 1 && (
                  <OnboardingWelcome userName={state.user_name} onNext={nextStep} />
                )}
                {state.currentStep === 2 && (
                  <OnboardingHighlights onNext={nextStep} onBack={prevStep} />
                )}
                {state.currentStep === 3 && (
                  <OnboardingConfig
                    state={state}
                    onUpdate={update}
                    onNext={nextStep}
                    onBack={prevStep}
                    validateCnpj={validateCnpj}
                  />
                )}
                {state.currentStep === 4 && (
                  <OnboardingConfirm
                    state={state}
                    onConfirm={provision}
                    onBack={prevStep}
                    loading={loading}
                    error={error}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>

      <p className="mt-4 text-xs text-muted-foreground relative z-10">
        © {new Date().getFullYear()} NetUno — Gestão de contratos de infraestrutura
      </p>
    </div>
  );
};

export default OnboardingPage;
